import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { NavigationFunction as RootNavigation, Pages } from "../../../../Navigation/Routers/root";
import { colors, css, fonts, frames, glyphs, useMediaQuery } from "../../../../Styles";
import { PageHeader, ButtonList, ProgressBar, Button, QuickEntryModal, AddAnimalModal, NewInput, NewInputHandles } from "../../../../Components";
import { ButtonTypes, ListData_ValueTypes, ListStyle_Types, NewFieldStyles, SelectFieldOption_Values, TableStyles_Types } from "../../../../Definitions";

// Table
import { useEditableColumnConstructor } from "../../../../Components/Tables/_Tables/useEditableColumnConstructor";
import { ColumnFiltersState, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, noop, Row, useReactTable } from "@tanstack/react-table";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";
import { GenericDataParameters, GenericRow } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import globalFilter from "../../../../Components/Tables/_Tables/useGlobalFilter";

// Data
import { useAppSelector } from "../../../../Store";
import { Graph_QL } from "../../../../Services";
import { useAnimal } from "../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { PanelMaintainAnimal } from "../../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/maintainAnimalInfoCall";
import { cloneDeep, keyBy, merge } from "lodash";
import { useSelectOptions } from "../../../../Components/_Components";

// TODO
import FilterPopup from "../../../../Components/Filter/filterPopup";
import FilterDisplayList from "../../../../Components/Filter/filterDisplayList";
import { useFeedCurves } from "../../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";

// const createConditionTableDivStyleFunction = (conditionData: Condition[]) => {
//   const colorTagFromName = (name: string|number) => {return conditionData.find(condition => condition.conditionName === name)?.conditionColorTag}
//   return (name?: string | number):CSSProperties|undefined => { if (!name) {return undefined} return {backgroundColor: colorTagFromName(name), ...css.colorTag, ...fonts.header, ...fonts.fontSize4, color: colors.whitePrimary}}
// }

interface AnimalTableData extends GenericRow {
  index: number;
  feedCurveId?: SelectFieldOption_Values;
}

export default function Animals() {
  const navigator = useNavigate();
  const { t } = useTranslation();
  let location = useLocation();
  const [editTable, setEditTable] = useState(false);
  const [isExcelData, setExcelData] = useState(false);
  const [isCsvData, setCsvData] = useState(false);
  const [quickEntryModal, setQuickEntryModal] = useState(false);
  const [addAnimalModal, setAddAnimalModal] = useState(false);
  const searchRef = useRef<NewInputHandles>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const mobileXs = useMediaQuery(`(max-width: 640px)`);
  const hideFilterDisplay = useMediaQuery(`(max-width: 765px), (min-width: 1001px) and (max-width: 1100px)`);
  // const divStyleOverride = useMemo(() => (createConditionTableDivStyleFunction(conditionData)),[]); // TODO
  const dirtRef = useRef<PanelMaintainAnimal[]>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const { feedcurveOptions, groupOptions, milestoneOptions } =
    useSelectOptions();
  const [autoReport, setAutoReport] = useState<number>(0);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { call: getAnimalsInfo, data: animalsState,  maintainEntries: updateAnimals } = useAnimal(connection);
  const { call: getFeedCurves, data: feedCurvesState } = useFeedCurves(connection);

  const [animals, setAnimals] = useState( animalsState && animalsState[domain] ? cloneDeep(animalsState[domain].data) : [] );
  const { locationOptions } = useSelectOptions();

  const feedCurves = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data;
    }
    return [];
  }, [domain, feedCurvesState]);

  useEffect(() => {
    getFeedCurves({ fetchAmount: 50, ifFeedCurveBaseInfoOnly: true });
    getAnimalsInfo({ fetchAmount: 50 }, connection);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAnimals(
      animalsState && animalsState[domain]
        ? cloneDeep(animalsState[domain].data)
        : []
    );
  }, [animalsState, domain]);

  useEffect(() => {
    const selected = location.state?.params?.selected;
    if (selected) {
      setAutoReport(selected);
    }
  }, [location.state]);

  useEffect(() => {
    setGlobalFilterValue(searchTerm);
  }, [searchTerm]);

  // #region | Animals Table
  const tableData: AnimalTableData[] = useMemo( () => animals.map((animal, index) => {
    const consumptionToday = animal.consumptions?.[0];
    const consumptionTotalToday = +((consumptionToday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedToday = +(consumptionTotalToday - (consumptionToday?.progressed ?? 0) / 1000).toFixed(2);
    const progressBarToday = ( <ProgressBar total={consumptionTotalToday} progressed={consumptionProgressedToday} /> );
    const progressTextToday = `${consumptionProgressedToday} / ${consumptionTotalToday}`;
    const consumptionYesterday = animal.consumptions?.[1];
    const consumptionTotalYesterday = +((consumptionYesterday?.total ?? 0) / 1000).toFixed(2);
    const consumptionProgressedYesterday = +(consumptionTotalYesterday - (consumptionYesterday?.progressed ?? 0) / 1000).toFixed(2);
    const progressBarYesterday = ( <ProgressBar total={consumptionTotalYesterday} progressed={consumptionProgressedYesterday} /> );
    const progressTextYesterday = `${consumptionTotalYesterday} / ${consumptionProgressedYesterday}`;

    const actionButton = ( <Button
      content={animal.animal_number.toString()}
      buttonType={ButtonTypes.textButton}
      startEnhancer={ <glyphs.ArrowSmooth fillColor={colors.greyDarkSecondary} /> }
      onClick={() => { RootNavigation(navigator, Pages.animals, Pages.animal, true, { selected: animal.animal_id }); }}
      overrides={{
        contentStyles: { color: colors.greyDarkPrimary, ...fonts.fontSize4, ...fonts.paragraph },
        startEnhancerStyles: { marginRight: "15px" },
      }}
    /> );

    return {
      action: isExcelData || isCsvData ? animal.animal_number : actionButton,
      id: animal.animal_id,
      // animalType: animal.animalType,
      life_number: animal.life_number,
      responderNumber: animal.responder_number ? animal.responder_number : "",
      // animalCondition: animal.condition? animal.condition : 0,
      systemId: animal.system,
      animalGroupId: {label: animal.animalGroup.name ?? "", id: animal.animalGroup.groupId}, //animal.animalGroup ? animal.animalGroup.name : "",
      animalLocation: animal.location_id ? locationOptions?.find(({ id }) => animal.location_id === id) : undefined,
      feedCurveId: { label: animal.feedCurve?.name ?? "", id: animal.feedCurve?.feedCurveId},
      animalRemainingToday: isExcelData || isCsvData ? progressTextToday : progressBarToday,
      animalRemainingYesterday: isExcelData || isCsvData ? progressTextYesterday : progressBarYesterday,
      milestoneId: { id: animal.milestone?.milestone_id, label: animal.milestone?.name ?? "" },
      animalMilestoneDay: animal.milestone_day ? Math.floor((new Date().getTime() - animal.milestone_day) / (1000 * 60 * 60 * 24)) : t("noData", { defaultValue: "no data" }),
      animalCycleDayTotal: animal.cycle,
      index,
    };
  }), [animals, isCsvData, isExcelData, locationOptions, navigator, t]);

  const accessorData = useMemo<GenericDataParameters<AnimalTableData>[]>( () => [
    { headerValue: t("params.animalId", { defaultValue: "animal no." }), accessor: "action", type: isExcelData || isCsvData ? ListData_ValueTypes.Number : ListData_ValueTypes.Button, width: "130px", staticColumn: true },
    // {headerValue: t("params.animalType", {defaultValue: "animal type"}), accessor: "animalType", type: ListData_ValueTypes.String, sort: true, width: "160px"},
    { headerValue: t("params.lifeNo", { defaultValue: "life no." }), accessor: "life_number", type: ListData_ValueTypes.Number, sort: true, width: "160px" },
    { headerValue: t("params.responderNo", { defaultValue: "responder no." }), accessor: "responderNumber", type: ListData_ValueTypes.Number, sort: true,  width: "200px" },
    // {headerValue: t("params.condition", {defaultValue: "condition"}), accessor: "animalCondition", type: ListData_ValueTypes.List, options: conditionOptions, sort: true, width: "160px", divStyleOverride},
    { headerValue: t("params.group", { defaultValue: "group" }), accessor: "animalGroupId", type: ListData_ValueTypes.List, options: groupOptions, sort: true, width: "160px", filter: true },
    { headerValue: t("params.location", { defaultValue: "location" }), accessor: "animalLocation", type: ListData_ValueTypes.List,  options: locationOptions, sort: true, width: "200px" },
    { headerValue: t("feed.feedCurve.feedCurve", { defaultValue: "feed curve", }), accessor: "feedCurveId", type: ListData_ValueTypes.List, options: feedcurveOptions, sort: true, width: "200px", filter: true },
    { headerValue: t("feed.general.remainingToday", { defaultValue: "remaining today", }), accessor: "animalRemainingToday", type: isExcelData || isCsvData ? ListData_ValueTypes.String : ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px" },
    { headerValue: t("feed.general.remainingYesterday", { defaultValue: "remaining yesterday", }), accessor: "animalRemainingYesterday", type: isExcelData || isCsvData ? ListData_ValueTypes.String : ListData_ValueTypes.ProgressBar, staticColumn: true, sort: true, width: "200px" },
    { headerValue: t("milestones.general.latestMilestone", { defaultValue: "curr. milestone", }), accessor: "milestoneId", type: ListData_ValueTypes.List,
      options: (row?: Row<AnimalTableData>) => {
        if (!row) {
          return milestoneOptions;
        }
        return (
          feedCurves
            .find(
              (feedCurve) =>
                row.original.feedCurveId?.id === feedCurve.feedCurveId
            )
            ?.milestones?.map((milestone) => ({
              id: milestone.feedCurveMilestoneId,
              label: milestone.name,
            })) ?? []
        );
      },
    sort: true, width: "200px", filter: true },
    { headerValue: t("milestones.general.milestoneDay", { defaultValue: "curr. milestone day", }), accessor: "animalMilestoneDay", type: ListData_ValueTypes.String, sort: true, width: "230px" },
    { headerValue: t("animalPages.animal.totalCycleDay", { efaultValue: "total cycle day", }),  accessor: "animalCycleDayTotal", type: ListData_ValueTypes.Number, sort: true,  width: "200px" },
    { headerValue: t("animalPages.animal.systemName", { defaultValue: "system", }), accessor: "systemId", type: ListData_ValueTypes.String, sort: true, width: "200px", staticColumn: true },
  ], [ feedCurves, feedcurveOptions, groupOptions, isCsvData, isExcelData, locationOptions, milestoneOptions, t ]);

  const columns = useEditableColumnConstructor({
    accessorData: [...accessorData],
    tableStyle: TableStyles_Types.primaryTable,
    canEdit: editTable,
  });

  const table = useReactTable({
    columns,
    data: tableData,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnFiltersChange: setColumnFilters,
    globalFilterFn: globalFilter,
    state: {
      globalFilter: globalFilterValue,
      columnFilters,
      columnPinning: {
        left: isExcelData || isCsvData ? [] : ["action", "id"],
      },
    },
    initialState: {
      pagination: {
        pageSize: 50,
      },
    },
    meta: {
      deleteRow: noop,
      // TODO REDUCE BOILER PLATE AND KEEP IT DRY
      updateData: (rowId, columnId, value, row) => {
        if (columnId === "responderNumber") {
          dirtRef.current = Object.values(
            merge(
              {},
              keyBy(dirtRef.current, "animalId"),
              keyBy(
                [{ animalId: rowId, [columnId]: Number(value) }],
                "animalId"
              )
            )
          );
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.responder_number = Number(value);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "feedCurveId") {
          const selectValue = value as any as SelectFieldOption_Values;
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];

          if (newAnimal.feedCurve) {
            newAnimal.feedCurve.feedCurveId = Number(selectValue.id);
            newAnimal.feedCurve.name = selectValue.label;
          } else {
            newAnimal.feedCurve = {
              name: selectValue.label,
              feedCurveId: Number(selectValue.id),
              feedLimit: 0,
              passFeed: 0,
              stimuli: 0,
            };
          }

          const selectedFeedCurve = feedCurves.find(
            (feedCurve) => feedCurve.feedCurveId === Number(selectValue.id)
          );
          if (selectedFeedCurve?.milestones?.length) {
            newAnimal.milestone = {
              name: selectedFeedCurve.milestones[0].name,
              milestone_id: selectedFeedCurve.milestones[0].milestoneId,
            };
            newAnimal.milestone_day = Date.now();
          } else {
            newAnimal.milestone = undefined;
          }

          dirtRef.current = Object.values( merge({},
            keyBy(dirtRef.current, "animalId"),
            keyBy([{
              animalId: rowId,
              feedCurveId: selectValue.id,
              fk_feedcurve_milestones: selectedFeedCurve?.milestones
                ?.length
                ? selectedFeedCurve?.milestones[0].feedCurveMilestoneId
                : undefined,
            }], "animalId")
          ));
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalCycleDayTotal") {
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animalId"),
            keyBy([{ animalId: rowId, cycle: Number(value) }], "animalId")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.cycle = Number(value);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "life_number") {
          dirtRef.current = Object.values( merge({},
            keyBy(dirtRef.current, "animalId"),
            keyBy([{ animalId: rowId, [columnId]: Number(value) }],"animalId")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.life_number = Number(value);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalGroupId") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtRef.current = Object.values( merge({},
            keyBy(dirtRef.current, "animalId"),
            keyBy([{ animalId: rowId, animal_group_id: selectValue.id }], "animalId")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          if (newAnimal.animalGroup) {
            newAnimal.animalGroup.groupId = Number(selectValue.id);
            newAnimal.animalGroup.name = selectValue.label;
          } else {
            newAnimal.animalGroup = {
              name: selectValue.label,
              groupId: Number(selectValue.id),
            };
          }
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalLocation") {
          const selectValue = value as any as SelectFieldOption_Values;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animalId"),
            keyBy([{ animalId: rowId, location: selectValue.id }], "animalId")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.location_id = Number(selectValue.id);
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "animalMilestoneDay") {
          const newValue = Date.now() - Number(value) * 86400000;
          dirtRef.current = Object.values(merge({},
            keyBy(dirtRef.current, "animalId"),
            keyBy([{animalId: rowId, milestone_day: Math.round(newValue / 1000),}],"animalId")
          ));
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex];
          newAnimal.milestone_day = newValue;
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
        if (columnId === "milestoneId") {
          const selectValue = value as any as SelectFieldOption_Values;
          const dataIndex = Number(row.original["index"]);
          const newAnimal = animals[dataIndex]; // selected value might not be correct
          if (row.original.feedCurveId?.id) {
            const feedCurveId = feedCurves.find(
              (feedCurve) =>
                feedCurve.feedCurveId === row.original.feedCurveId?.id
            );
            const milestone = feedCurveId?.milestones?.find(
              (milestone) =>
                milestone.feedCurveMilestoneId === Number(selectValue.id)
            );
            if (milestone) {
              newAnimal.milestone = {
                milestone_id: milestone.milestoneId,
                name: milestone.name,
              };
              newAnimal.milestone_day = Date.now();
              dirtRef.current = Object.values( merge({},
                keyBy(dirtRef.current, "animalId"),
                keyBy([{ animalId: rowId, fk_feedcurve_milestones: milestone.feedCurveMilestoneId }], "animalId")
              ));
            }}
          animals.splice(dataIndex, 1, newAnimal);
          setAnimals(cloneDeep(animals));
          return;
        }
      },
    },
  });
  const reactiveTable = ReactiveTable({
    table,
    tableType: TableStyles_Types.primaryTable,
    emptyText: t("animalPages.animals.emptyText", {
      defaultValue: "No Animal Found",
    }),
    renderFooter: false,
  });
  // #endregion

  // #region | Download
  // #region | Download CSV
  const exportToCSV = useCallback(
    (data: { [key: string]: any }[], filename = `${t("animalPages.animals.animals", { defaultValue: "animals", })}.csv` ) => {
      if (!data.length) return;

      const headers = accessorData.map((col) => col.headerValue);
      const rows = data.map((row) => accessorData.map((col) => row[col.accessor] ?? "") );
      const csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((row) => row.join(","))].join("\n");

      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [accessorData, t]
  );

  const handleExportCSV = () => {
    setCsvData(true);
  };
  // #endregion

  // #region | Download Excel
  const exportToExcel = useCallback(
    (data: { [key: string]: any }[],filename = `${t("animalPages.animals.animals", {defaultValue: "animals"})}.xlsx`) => {
      if (!data.length) return;

      const headers = accessorData.map((col) => col.headerValue);
      const rows = data.map((row) => accessorData.map((col) => row[col.accessor] ?? ""));
      
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      worksheet["!cols"] = accessorData.map(() => ({ wpx: 100 }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet( workbook, worksheet, t("animalPages.animals.animals", { defaultValue: "animals" }));
      XLSX.writeFile(workbook, filename);
    },
    [accessorData, t]
  );

  const handleExportExcel = () => {
    setExcelData(true);
  };
  // #endregion

  useEffect(() => {
    if (isExcelData) {
      exportToExcel(tableData);
      setExcelData(false);
    } else if (isCsvData) {
      exportToCSV(tableData);
      setCsvData(false);
    }
  }, [exportToCSV, exportToExcel, isCsvData, isExcelData, tableData]);
  // #endregion

  return (
    <frames.Page_inner>
      <frames._page_inner_header>
        <div style={{ display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          <PageHeader
            topHeader={t(`menuHeaders.${Pages.animals}`, { defaultValue: "animals" })}
            subHeader={t("menuHeaders.overview", { defaultValue: "overview" })}
          />
          <div style={{ display: "block", zIndex: "999" }}>
            <ButtonList
              contents={[ t("animalPages.animals.quickEntry", { defaultValue: "Quick Entry" }) ]}
              onClick={() => setQuickEntryModal(true)}
              buttonType={ButtonTypes.primaryButton}
              overrides={{ baseButton: { ...(!mobileXs ? { ...css.button_FitContent, minWidth: "none" } : { backgroundColor: colors.greyDarkSecondary }) }}}
              listOverrides={{ listStyle: !mobileXs ? ListStyle_Types.horizontal : ListStyle_Types.vertical }}
              responsiveOverrides={{
                isResponsive: !mobileXs ? false : true,
                popoverStyle: { backgroundColor: colors.greyDarkPrimary, padding: "20px", borderRadius: "10px", boxShadow: "0px 0px 5px 2px" + colors.blackPrimary + "4d" },
              }}
            />
          </div>
        </div>
      </frames._page_inner_header>
      <frames._page_inner_content>
        <div
          style={{ height: "100%", width: "100%", display: "grid", gridTemplateRows: mobileXs ? "85px 1fr 35px" : "50px 1fr 35px",  boxSizing: "border-box" }}>
          {/* <div style={{ gridArea: "1/1/2/2", backgroundColor: "transparent", display: "flex", justifyContent: "space-between", padding: "0px 20px", alignItems: "end" }}> */}
          <div style={{gridArea: "1/1/2/2", boxSizing: "border-box", width: "100%", padding: "0px 20px", display: "grid", gridTemplateColumns: mobileXs ? "1fr" : "280px 1fr 272px", gridTemplateRows: mobileXs ? "35px 50px" : "50px"}}>
            <div style={{ marginBottom: "5px", gridArea: "1/1/2/2", display: "flex", alignItems: "end" }}>
              <NewInput ref={searchRef}
                onChange={(value: string) => {setSearchTerm(value)}}
                fieldOverrides={{fieldStyle: NewFieldStyles.greySecondary}}
                placeholder={t("search", { defaultValue: "search" })}
              />
            </div>
            {hideFilterDisplay ? <></>
            : <div style={{ gridArea: "1/2/2/3", display: "flex", alignItems: "end", position: "relative" }}>
                <FilterDisplayList filters={columnFilters} setColumnFilters={setColumnFilters} />
              </div> }
            <div style={{ display: "flex", justifyContent: "end", gap: "5px", gridArea: mobileXs ? "2/1/3/2" : "1/3/2/4" }}>
              <FilterPopup<AnimalTableData> accessorData={accessorData} table={table} autoReport={autoReport ?? undefined} />
              {!editTable ? (
                <ButtonList
                  contents={[
                    t("buttons.add", { defaultValue: "add" }),
                    t("buttons.edit", { defaultValue: "edit" }),
                    t("buttons.excel", { defaultValue: "excel" }),
                    t("buttons.csv", { defaultValue: "csv" }),
                  ]}
                  onClick={(i) => {
                    if (i === 0) { setAddAnimalModal(true) }
                    if (i === 1) { setEditTable(true) }
                    if (i === 2) { handleExportExcel() }
                    if (i === 3) { handleExportCSV() }
                  }}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[ glyphs.Add({}), glyphs.Edit({}), glyphs.Close({}), glyphs.Close({}) ]}
                />
              ) : (
                <ButtonList
                  contents={[
                    t("buttons.save", { defaultValue: "save" }),
                    t("buttons.cancel", { defaultValue: "cancel" }),
                    t("buttons.add", { defaultValue: "add" }),
                    t("buttons.edit", { defaultValue: "edit" }),
                    t("buttons.excel", { defaultValue: "excel" }),
                    t("buttons.csv", { defaultValue: "csv" }),
                  ]}
                  onClick={(i) => {
                    if (i === 0) { if (updateAnimals) { updateAnimals({ data: dirtRef.current }) } dirtRef.current = []; setEditTable(false)}
                    if (i === 1) { setAnimals(animalsState && animalsState[domain] ? cloneDeep(animalsState[domain].data) : []); dirtRef.current = []; setEditTable(false);}
                    if (i === 2) {}
                    if (i === 3) {}
                    if (i === 4) {}
                    if (i === 5) {}
                  }}
                  buttonType={ButtonTypes.tableButton}
                  startEnhancers={[ glyphs.Check({}), glyphs.Close({}), glyphs.Add({}), glyphs.Edit({}), glyphs.Close({}), glyphs.Close({}) ]}
                  disabled={[false, false, true, true, true, true]}
                  overrides={(index) => ({
                    baseButton: { backgroundColor: index === 3 ? colors.greyPrimary : colors.greySecondary },
                    disabledStyles: { opacity: index === 2 || index > 3 ? 0.5 : 1 },
                  })}
                />
              )}
            </div>
          </div>
          <div style={{ gridArea: "2/1/3/2", height: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid " + colors.greyPrimary, borderRadius: "10px", overflow: "hidden" }}>
            {/* <div style={{backgroundColor: colors.greyPrimary, display: "flex", justifyContent: "start"}}>
            <NewInput
              ref={searchRef}
              onChange={(value: string) => {
                setSearchTerm(value);
                console.log("Search Term:", value); // Tilføj denne linje til debugging
              }}
                fieldOverrides={{
                fieldStyle: NewFieldStyles.greySecondary
              }}
              placeholder={t("search", { defaultValue: "search" })}
              />
            </div> */}
            {reactiveTable}
          </div>
          <div
            style={{ gridArea: "3/1/4/2", display: "flex", alignItems: "center", gap: "5px", padding: "0px 20px" }}>
            <Button
              content={""}
              onClick={() => { if (table.getCanPreviousPage()) table.previousPage() }}
              startEnhancer={glyphs.Arrow({ rotate: 180 })}
              overrides={{
                baseButton: { height: "30px",  width: "35px", backgroundColor: colors.greyPrimary },
                hoverStyles: { backgroundColor: colors.greySecondary },
                startEnhancerStyles: { margin: "0px", filter: table.getCanPreviousPage() ? "opacity(1)" : "opacity(0.3)", transition: "filter 0.2s ease" },
              }}
            />
            <Button
              content={""}
              onClick={() => { if (table.getCanNextPage()) table.nextPage() }}
              startEnhancer={glyphs.Arrow({})}
              overrides={{
                baseButton: { height: "30px",  width: "35px", backgroundColor: colors.greyPrimary },
                hoverStyles: { backgroundColor: colors.greySecondary },
                startEnhancerStyles: { margin: "0px", filter: table.getCanNextPage() ? "opacity(1)" : "opacity(0.3)", transition: "filter 0.2s ease" },
              }}
            />
            <p style={{ ...fonts.fontSize4, ...fonts.header, color: colors.greyLightTertiary }}>
              {`${t("page", { defaultValue: "page" })} ${table.getState().pagination.pageIndex + 1} ${t("of", { defaultValue: "of" })} ${table.getPageCount()}`}
            </p>
          </div>
        </div>
      </frames._page_inner_content>

      <AddAnimalModal
        isModalOpen={addAnimalModal}
        closeModal={() => setAddAnimalModal(false)}
      />
      <QuickEntryModal
        isModalOpen={quickEntryModal}
        closeModal={() => setQuickEntryModal(false)}
      />
    </frames.Page_inner>
  );
}
